import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import type { CSSProperties } from "react";

import type {
  RetellCallsCareSpaceBreakdownResponse,
  RetellCallsSummaryResponse,
  RetellCallsTransferReportResponse,
} from "./retellCallsTypes";
import {
  CareSpaceAgeCohortSection,
  ClinicBreakdownSection,
  TransferReportSection,
} from "./retellCallsSections";
import {
  DEEP,
  FAINT,
  GOAL_PCT,
  GOLD,
  GRID,
  INK,
  MUTED,
  ORANGE,
  SKY,
  TEAL,
  fmtDateLabel,
  fmtDuration,
  fmtInt,
  fmtPct,
  pct,
} from "./retellCallsTheme";
import { LegendSwatch, MetricCard, MtdBand, Section, StatCard } from "./retellCallsUi";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
);

const responsiveTwoCol: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
  gap: "0.9rem",
  marginBottom: "0.9rem",
  minWidth: 0,
};

function DeferredSectionState({
  label,
  error,
  onRetry,
}: {
  error?: string | undefined;
  label: string;
  onRetry?: (() => void) | undefined;
}) {
  return (
    <Section title={label}>
      <div
        role={error ? "alert" : "status"}
        className={
          error
            ? "rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            : "rounded-md border border-border/70 bg-background/70 p-4 text-sm text-muted-foreground"
        }
      >
        {error ? `${label} could not load: ${error}` : `Loading ${label.toLowerCase()}…`}
        {error && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="ml-3 rounded-md border border-red-300 bg-white px-2 py-1 font-medium"
          >
            Retry this section
          </button>
        ) : null}
      </div>
    </Section>
  );
}

export function RetellCallsReadout({
  data,
  transfer,
  careSpace,
  transferError,
  careSpaceError,
  retryTransfer,
  retryCareSpace,
}: {
  careSpace?: RetellCallsCareSpaceBreakdownResponse;
  careSpaceError?: string;
  data: RetellCallsSummaryResponse;
  retryCareSpace?: () => void;
  retryTransfer?: () => void;
  transfer?: RetellCallsTransferReportResponse;
  transferError?: string;
}) {
  const { core, categories, disconnections, hourly, trend, longestCall, longNonIntake } = data;

  const answered = core.live;
  const trendLabels = trend.map((r) => fmtDateLabel(r.day));
  const maxCalls = Math.max(...categories.map((c) => c.calls), 1);

  const displayHours = Array.from({ length: 24 }, (_, h) => {
    if (h === 0) return "12am";
    if (h === 12) return "12pm";
    return h < 12 ? `${h}am` : `${h - 12}pm`;
  });

  const longNonIntakeMix = longNonIntake.categories
    .map((c) => `${c.category} (${fmtInt(c.calls)})`)
    .join(", ");

  return (
    <div style={{ minWidth: 0, width: "100%" }}>
      {/* Headline */}
      <div style={{ fontSize: "1rem", color: INK, marginBottom: "1rem" }}>
        <b style={{ color: TEAL, fontWeight: 800 }}>{fmtInt(core.intakes)}</b> intakes scheduled
        from <b style={{ color: TEAL, fontWeight: 800 }}>{fmtInt(answered)}</b> answered calls
        across <b style={{ color: TEAL, fontWeight: 800 }}>{fmtInt(core.uniqueNumbers)}</b> unique
        numbers
      </div>

      {/* Month-to-date band */}
      {core.mtd && <MtdBand mtd={core.mtd} />}

      {/* KPI cards */}
      <div
        style={{
          display: "flex",
          gap: "0.65rem",
          flexWrap: "wrap",
          marginBottom: "1rem",
        }}
      >
        <MetricCard
          label="Ended calls"
          value={fmtInt(core.ended)}
          sub={`${fmtInt(core.outbound)} outbound · ${fmtInt(core.inbound)} inbound`}
          accent={TEAL}
        />
        <MetricCard
          label="Answered"
          value={fmtInt(answered)}
          sub={`${fmtPct(pct(answered, core.ended))} live/productive`}
          accent={SKY}
        />
        <MetricCard
          label="Intakes scheduled"
          value={fmtInt(core.intakes)}
          sub={`${fmtPct(pct(core.intakes, answered))} of answered`}
          accent={ORANGE}
        />
        <MetricCard
          label="Transfers"
          value={fmtInt(core.transfers)}
          sub={`${fmtPct(pct(core.transfers, answered))} of answered`}
          accent={DEEP}
        />
        <MetricCard
          label="VM / IVR"
          value={fmtInt(core.vmIvr)}
          sub={`${fmtPct(pct(core.vmIvr, core.ended))} of ended`}
          accent={GOLD}
        />
        <MetricCard
          label="Median call"
          value={`${Math.round(core.medianDurationSec)}s`}
          sub="ended calls only"
          accent={FAINT}
        />
      </div>

      {/* Charts row */}
      <div style={responsiveTwoCol}>
        {/* 14-day volume with intake rate */}
        <Section
          title="Daily call volume with intake rate"
          legend={
            <>
              <LegendSwatch color={TEAL} label="calls" />
              <LegendSwatch color={ORANGE} label="intake rate" />
            </>
          }
        >
          <div style={{ position: "relative", height: 200 }}>
            <Bar
              data={
                {
                  labels: trendLabels,
                  datasets: [
                    {
                      type: "bar",
                      label: "Calls",
                      data: trend.map((r) => r.calls),
                      backgroundColor: trend.map((_, i) => (i === trend.length - 1 ? DEEP : TEAL)),
                      yAxisID: "y",
                      order: 3,
                    },
                    {
                      type: "line",
                      label: "Intake rate %",
                      data: trend.map((r) => r.intakeRate),
                      borderColor: ORANGE,
                      backgroundColor: "transparent",
                      pointBackgroundColor: ORANGE,
                      pointRadius: 3,
                      borderWidth: 2.4,
                      yAxisID: "y2",
                      order: 1,
                    },
                    {
                      type: "line",
                      label: "Goal",
                      data: trend.map(() => GOAL_PCT),
                      borderColor: ORANGE,
                      borderDash: [3, 4],
                      borderWidth: 1.2,
                      pointRadius: 0,
                      yAxisID: "y2",
                      order: 2,
                    },
                  ],
                } as any
              }
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    filter: (ctx) => ctx.dataset.label !== "Goal",
                    callbacks: {
                      label: (ctx) => {
                        const y = ctx.parsed.y;
                        if (typeof y !== "number") return String(ctx.dataset.label ?? "");
                        return ctx.dataset.label === "Intake rate %"
                          ? `Intake rate: ${y.toFixed(1)}%`
                          : `Calls: ${y}`;
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    grid: { color: GRID },
                    ticks: { color: MUTED, font: { size: 11 } },
                  },
                  y2: {
                    position: "right",
                    grid: { display: false },
                    ticks: {
                      color: ORANGE,
                      font: { size: 11 },
                      callback: (v) => `${Number(v).toFixed(0)}%`,
                    },
                    suggestedMin: 0,
                  },
                  x: {
                    grid: { display: false },
                    ticks: { color: MUTED, font: { size: 10 } },
                  },
                },
              }}
            />
          </div>
        </Section>

        {/* Intraday pacing */}
        <Section
          title="Intraday pacing"
          legend={
            <>
              <LegendSwatch color={TEAL} label="calls" />
              <LegendSwatch color={ORANGE} label="intakes" />
            </>
          }
        >
          <div style={{ position: "relative", height: 200 }}>
            <Bar
              data={
                {
                  labels: displayHours,
                  datasets: [
                    {
                      type: "bar",
                      label: "Calls",
                      data: hourly.map((r) => r.calls),
                      backgroundColor: TEAL,
                      yAxisID: "y",
                      order: 2,
                    },
                    {
                      type: "line",
                      label: "Intakes",
                      data: hourly.map((r) => r.intakes),
                      borderColor: ORANGE,
                      backgroundColor: "transparent",
                      pointBackgroundColor: ORANGE,
                      pointRadius: 3,
                      borderWidth: 2.4,
                      yAxisID: "y2",
                      order: 1,
                    },
                  ],
                } as any
              }
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                  y: {
                    grid: { color: GRID },
                    ticks: { color: MUTED, font: { size: 10 } },
                  },
                  y2: {
                    position: "right",
                    grid: { display: false },
                    ticks: { color: ORANGE, font: { size: 10 } },
                    suggestedMin: 0,
                  },
                  x: {
                    grid: { display: false },
                    ticks: {
                      color: MUTED,
                      font: { size: 9 },
                      maxRotation: 0,
                      callback: (_: unknown, i: number) => (i % 4 === 0 ? displayHours[i] : ""),
                    },
                  },
                },
              }}
            />
          </div>
        </Section>
      </div>

      {/* Bottom row */}
      <div style={responsiveTwoCol}>
        {/* Top outcomes */}
        <Section title="Top call outcomes">
          <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
            {categories.map(({ category, calls: count }) => (
              <div
                key={category}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <div
                  style={{
                    width: 130,
                    fontSize: "0.72rem",
                    color: INK,
                    fontWeight: 600,
                    textAlign: "right",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {category}
                </div>
                <div
                  style={{
                    flex: 1,
                    background: GRID,
                    borderRadius: 4,
                    height: 8,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct(count, maxCalls)}%`,
                      background: TEAL,
                      height: "100%",
                      borderRadius: 4,
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "0.72rem",
                    color: MUTED,
                    fontWeight: 700,
                    width: 42,
                    textAlign: "right",
                  }}
                >
                  {fmtInt(count)}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Readout notes */}
        <Section title="Readout notes">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
              gap: "0.6rem",
              minWidth: 0,
            }}
          >
            <StatCard
              label="Corrected VM / IVR"
              value={fmtInt(core.vmIvr)}
              sub={`Raw Retell Voicemail was ${fmtInt(core.vmRaw)}; ${fmtInt(core.machineOverrides)} machine-label overrides.`}
            />
            <StatCard
              label="Top disconnect"
              value={disconnections[0] ? fmtInt(disconnections[0].calls) : "—"}
              sub={disconnections[0] ? disconnections[0].reason : "No disconnect reason data."}
            />
            <StatCard
              label="Longest ended call"
              value={longestCall ? fmtDuration(longestCall.durationSec) : "—"}
              sub={longestCall ? longestCall.category : "No duration data."}
            />
            <StatCard
              label="Long non-intake"
              value={fmtInt(longNonIntake.total)}
              sub={
                longNonIntake.total > 0
                  ? `Top outcomes: ${longNonIntakeMix}`
                  : "None over 60s without an intake."
              }
            />
          </div>
        </Section>
      </div>

      <div style={{ display: "grid", gap: "0.9rem", minWidth: 0 }}>
        {transfer && !transferError ? (
          <TransferReportSection rows={transfer.transferReport} definitions={transfer.definition} />
        ) : (
          <DeferredSectionState
            label="Transfer report"
            error={transferError}
            onRetry={retryTransfer}
          />
        )}
        {careSpace && !careSpaceError ? (
          <>
            <CareSpaceAgeCohortSection rows={careSpace.careSpaceAgeCohorts} />
            <ClinicBreakdownSection rows={careSpace.clinicBreakdown} />
          </>
        ) : (
          <DeferredSectionState
            label="CareSpace and clinic details"
            error={careSpaceError}
            onRetry={retryCareSpace}
          />
        )}
      </div>
    </div>
  );
}
